/** 9.4 Cobranza y GRATIVA */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";

export default function CollectionsReports() {
  const [data, setData] = useState(null);
  const [inversion, setInversion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rango, setRango] = useState({ desde: defaultDate(-30), hasta: defaultDate(0) });

  const load = async () => {
    setLoading(true);
    try {
      const [c, inv] = await Promise.all([
        api.get(`/reports/cobranza/comisiones-rtc`, { params: rango }),
        api.get(`/reports/cobranza/inversion-proveedor`, { params: rango }).catch(() => ({ data: null })),
      ]);
      setData(c.data);
      setInversion(inv.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rango]);

  return (
    <div className="space-y-5">
      <div className="bg-card border border-border/60 rounded-lg p-4 flex gap-3">
        <Field label="Desde"><input type="date" value={rango.desde} onChange={(e) => setRango({...rango, desde: e.target.value})} className="bg-app border border-border rounded-md px-2 py-1.5 text-sm" /></Field>
        <Field label="Hasta"><input type="date" value={rango.hasta} onChange={(e) => setRango({...rango, hasta: e.target.value})} className="bg-app border border-border rounded-md px-2 py-1.5 text-sm" /></Field>
      </div>

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Kpi label="Cobranza total" value={fmtL(data.totales.cobranza)} accent="green" />
          <Kpi label="GRATIVA devengada" value={fmtL(data.totales.comision_devengada)} accent="yellow" />
          <Kpi label="GRATIVA pagada" value={fmtL(data.totales.comision_pagada)} accent="muted" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border/60">
            <h3 className="font-heading text-sm">Cobranza y GRATIVA por RTC</h3>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-sidebar/60 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2">RTC</th><th className="p-2 text-right">Cobranza</th>
                  <th className="p-2 text-right"># Fact.</th>
                  <th className="p-2 text-right">GRATIVA deveng.</th>
                  <th className="p-2 text-right">GRATIVA pagada</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan="5" className="p-6 text-center text-text-muted">Cargando…</td></tr>
                : (data?.rtc || []).length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-text-muted">Sin datos</td></tr>
                : data.rtc.map((r, i) => (
                  <tr key={i} className="border-t border-border/30">
                    <td className="p-2">{r.rtc}<div className="text-[10px] text-text-muted">{r.regiones.join(", ") || "—"}</div></td>
                    <td className="p-2 text-right font-medium text-agro-green">{fmtL(r.cobranza)}</td>
                    <td className="p-2 text-right">{r.facturas_cobradas}</td>
                    <td className="p-2 text-right text-agro-yellow">{fmtL(r.comision_devengada)}</td>
                    <td className="p-2 text-right">{fmtL(r.comision_pagada)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border/60">
            <h3 className="font-heading text-sm">Cobranza por región</h3>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-sidebar/60 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2">Región</th>
                  <th className="p-2 text-right">Cobranza</th>
                  <th className="p-2 text-right">% del total</th>
                  <th className="p-2 text-right"># Facturas</th>
                </tr>
              </thead>
              <tbody>
                {(data?.regiones || []).map((r, i) => {
                  const totalCob = (data?.regiones || []).reduce((s, x) => s + x.cobranza, 0) || 1;
                  return (
                    <tr key={i} className="border-t border-border/30">
                      <td className="p-2">{r.region}</td>
                      <td className="p-2 text-right font-medium">{fmtL(r.cobranza)}</td>
                      <td className="p-2 text-right text-agro-green">{((r.cobranza / totalCob) * 100).toFixed(1)}%</td>
                      <td className="p-2 text-right">{r.facturas}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {inversion && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="inversion-proveedor">
          <div className="p-4 border-b border-border/60 flex justify-between items-center">
            <h3 className="font-heading text-sm">Inversión por proveedor (compras + gastos de contado)</h3>
            <div className="text-[10px] text-text-muted">Total {fmtL(inversion.totales.total)} · Gastos contado {fmtL(inversion.totales.gastos_contado)}</div>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-sidebar/60 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2">Proveedor</th><th className="p-2 text-right">Compras</th>
                  <th className="p-2 text-right">Gastos contado</th><th className="p-2 text-right">Total</th>
                  <th className="p-2 text-right">%</th><th className="p-2 text-right"># Docs</th>
                </tr>
              </thead>
              <tbody>
                {inversion.rows.length === 0 ? <tr><td colSpan="6" className="p-6 text-center text-text-muted">Sin datos</td></tr>
                : inversion.rows.map((r, i) => (
                  <tr key={i} className="border-t border-border/30">
                    <td className="p-2">{r.proveedor}</td>
                    <td className="p-2 text-right">{fmtL(r.compras)}</td>
                    <td className="p-2 text-right">{fmtL(r.gastos_contado)}</td>
                    <td className="p-2 text-right font-medium text-agro-yellow">{fmtL(r.total)}</td>
                    <td className="p-2 text-right">{r.participacion_pct}%</td>
                    <td className="p-2 text-right">{r.documentos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  const c = accent === "green" ? "text-agro-green" : accent === "yellow" ? "text-agro-yellow" : "text-text-secondary";
  return <div className="bg-card border border-border/60 rounded-lg p-4">
    <div className="text-[9px] uppercase tracking-wider text-text-muted">{label}</div>
    <div className={`font-heading text-2xl mt-0.5 ${c}`}>{value}</div>
  </div>;
}

function Field({ label, children }) {
  return <div>
    <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">{label}</label>
    {children}
  </div>;
}

function defaultDate(d) {
  const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10);
}
